import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { memo, useEffect, useState, type FormEvent } from "react";

interface SavedListNameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  submitLabel: string;
  /** Prefilled name (e.g. the current name for rename, a date default for save-cart). */
  defaultName?: string;
  isSubmitting?: boolean;
  error?: string | null;
  onSubmit: (name: string) => void;
}

/**
 * A single-field "name your list" dialog, shared by create / rename / save-cart.
 * The name resets to `defaultName` each time the dialog opens.
 */
export const SavedListNameDialog = memo(function SavedListNameDialog({
  open,
  onOpenChange,
  title,
  description,
  submitLabel,
  defaultName = "",
  isSubmitting = false,
  error,
  onSubmit
}: SavedListNameDialogProps) {
  const [name, setName] = useState(defaultName);

  useEffect(() => {
    if (open) setName(defaultName);
  }, [open, defaultName]);

  const trimmed = name.trim();

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (trimmed && !isSubmitting) onSubmit(trimmed);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description ? (
              <DialogDescription>{description}</DialogDescription>
            ) : null}
          </DialogHeader>

          <div className="py-4">
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="List name"
              aria-label="List name"
              maxLength={100}
              autoFocus
            />
            {error ? (
              <p
                role="alert"
                className="mt-2 text-sm text-red-600 dark:text-red-400"
              >
                {error}
              </p>
            ) : null}
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!trimmed || isSubmitting}>
              {isSubmitting ? "Saving…" : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
});
